import React, { useMemo, useState } from 'react';
import { X, Lock, ChevronRight } from 'lucide-react';
import { buildBootcampUrl, getLeadEmail, saveLeadEmail } from '../utils/leadCapture';

function summarizeRoadmap(roadmap) {
  const items = roadmap?.items || [];
  const totalMonths = roadmap?.totalMonths || 0;
  const certs = items.filter((i) => i?.type === 'certification');
  const certCount = certs.length;
  const certCost = certs.reduce((sum, c) => sum + (Number(c.cost) || 0), 0);
  return { totalMonths, certCount, certCost };
}

function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-[100]">
      <div
        className="absolute inset-0 bg-black/70"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="w-full max-w-lg rounded-2xl border border-slate-800 bg-slate-950 text-white shadow-2xl">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800">
            <div className="font-bold text-lg">{title}</div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-2 hover:bg-white/5"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="px-5 py-5">{children}</div>
        </div>
      </div>
    </div>
  );
}

export default function RoadmapGate({ roadmap, roleKey, children, teaserMonths = 2 }) {
  const [email, setEmail] = useState('');
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [status, setStatus] = useState({ state: 'idle', message: '' });
  const [isOpen, setIsOpen] = useState(false);
  const [leadEmail, setLeadEmailState] = useState(() => getLeadEmail());

  const summary = useMemo(() => summarizeRoadmap(roadmap), [roadmap]);

  if (!roadmap) return null;

  const unlock = async () => {
    setStatus({ state: 'loading', message: '' });
    const res = await saveLeadEmail({ email, roleKey, marketingConsent });
    if (!res.ok) {
      setStatus({ state: 'error', message: res.error || 'Something went wrong.' });
      return;
    }
    const stored = getLeadEmail();
    setLeadEmailState(stored);
    setStatus({ state: 'success', message: 'Unlocked. Redirecting to your roadmap…' });
    setIsOpen(false);
  };

  const renderChildren = (opts) => {
    if (typeof children === 'function') return children({ ...opts, roleKey });
    return React.cloneElement(children, { ...opts, roleKey });
  };

  if (leadEmail) {
    const bootcampUrl = buildBootcampUrl({ utmMedium: 'roadmap', utmCampaign: 'bootcamp' });
    return (
      <>
        {renderChildren({ isUnlocked: true, maxMonth: undefined })}
        {/* Sticky funnel CTA */}
        <div className="fixed bottom-0 left-0 right-0 z-[60]">
          <div className="mx-auto max-w-6xl px-4 pb-4">
            <div className="rounded-2xl border border-green-400/20 bg-slate-950/80 backdrop-blur shadow-2xl px-4 py-3 flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <div className="flex-1">
                <div className="text-sm font-bold text-white">Want to fast‑track this roadmap?</div>
                <div className="text-xs text-slate-300">Join Cyber Agoge — guided DevSecOps and AI security learning with practical projects.</div>
              </div>
              <a
                href={bootcampUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center rounded-xl bg-green-400 px-5 py-3 font-extrabold text-slate-950 hover:bg-green-300 transition-colors w-full sm:w-auto"
              >
                Go to Bootcamp
                <ChevronRight className="ml-2 h-5 w-5" />
              </a>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="relative">
      <div className="pointer-events-none select-none">
        {renderChildren({ isUnlocked: false, maxMonth: teaserMonths })}
      </div>

      <div className="absolute inset-0 z-[20] flex items-start justify-center p-4 sm:p-8">
        <div className="w-full max-w-2xl rounded-2xl border border-slate-800 bg-slate-950/80 backdrop-blur shadow-2xl p-6">
          <div className="flex items-start gap-3">
            <div className="mt-1 rounded-xl bg-green-400/10 border border-green-400/20 p-2">
              <Lock className="h-5 w-5 text-green-300" />
            </div>
            <div className="flex-1">
              <div className="text-xl font-extrabold text-white">
                Your roadmap is ready — unlock it free
              </div>
              <div className="mt-1 text-sm text-slate-300">
                Enter your email to reveal the full roadmap and download it as a PDF.
              </div>

              <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
                  <div className="text-xs uppercase tracking-wide text-slate-400">Timeline</div>
                  <div className="mt-1 text-lg font-bold text-green-300">
                    {summary.totalMonths || '—'} months
                  </div>
                </div>
                <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
                  <div className="text-xs uppercase tracking-wide text-slate-400">Certifications</div>
                  <div className="mt-1 text-lg font-bold text-green-300">
                    {summary.certCount}
                  </div>
                </div>
                <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
                  <div className="text-xs uppercase tracking-wide text-slate-400">Est. cert cost</div>
                  <div className="mt-1 text-lg font-bold text-green-300">
                    £{Math.round(summary.certCost)}
                  </div>
                </div>
              </div>

              <div className="mt-5 flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={() => setIsOpen(true)}
                  className="inline-flex items-center justify-center rounded-xl bg-green-400 px-5 py-3 font-bold text-slate-950 hover:bg-green-300 transition-colors"
                >
                  Unlock full roadmap (free)
                  <ChevronRight className="ml-2 h-5 w-5" />
                </button>
                <div className="text-xs text-slate-400 sm:self-center">
                  Marketing updates are optional. Your roadmap opens here after saving.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {isOpen && (
        <Modal title="Unlock your roadmap" onClose={() => setIsOpen(false)}>
          <div className="space-y-4">
            <div className="text-sm text-slate-300">
              Enter your email to open the roadmap. Choose below whether you also want learning updates.
            </div>

            <label className="block">
              <div className="text-sm font-semibold text-slate-200 mb-2">Email address</div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="w-full px-4 py-3 rounded-xl bg-slate-900 border border-slate-700 text-white focus:outline-none focus:ring-1 focus:ring-green-400"
              />
            </label>

            <label className="flex items-start gap-2 text-sm text-slate-300">
              <input type="checkbox" checked={marketingConsent} onChange={e => setMarketingConsent(e.target.checked)} />
              Send me Tekyu learning updates by email (optional). Unsubscribe at any time.
            </label>
            {status.state === 'error' && (
              <div className="text-sm text-red-400">{status.message}</div>
            )}

            <button
              type="button"
              onClick={unlock}
              disabled={status.state === 'loading'}
              className="w-full inline-flex items-center justify-center rounded-xl bg-green-400 px-5 py-3 font-bold text-slate-950 hover:bg-green-300 transition-colors disabled:opacity-60"
            >
              {status.state === 'loading' ? 'Unlocking…' : 'Unlock & view roadmap'}
            </button>

            <div className="text-xs text-slate-400">
              Your roadmap opens here; it is not sent by email. See our{' '}
              <a className="underline hover:text-slate-200" href="/privacy-policy">
                Privacy Policy
              </a>
              .
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

